import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import { User } from '../../types';
import { prefetchAllData } from '../../utils/prefetchData';
import { STORAGE_KEYS } from '../../utils/constants';
import AppPreloader from '../../components/common/AppPreloader';
import './Login.css';
import ForgotPassword from './ForgotPassword';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isPrefetching, setIsPrefetching] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await authService.login(email, password);
      const userData: User = {
        id: res.user.id,
        name: res.user.name,
        email: res.user.email,
        role: 'user',
      };
      login(userData, res.token);
      
      // Запускаем предзагрузку данных с показом прелоадера
      setIsPrefetching(true);
      try {
        await prefetchAllData(queryClient);
        // Устанавливаем флаг, что prefetch завершен, чтобы App.tsx не запускал его повторно
        localStorage.setItem(STORAGE_KEYS.PREFETCH_COMPLETED, 'true');
      } catch (prefetchError) {
        // Игнорируем ошибки предзагрузки - данные загрузятся при необходимости
        console.warn('Ошибка при предзагрузке данных:', prefetchError);
        // Все равно устанавливаем флаг, чтобы не блокировать навигацию
        localStorage.setItem(STORAGE_KEYS.PREFETCH_COMPLETED, 'true');
      } finally {
        setIsPrefetching(false);
        // Переходим на дашборд только после завершения предзагрузки
        navigate('/dashboard', { replace: true });
      }
    } catch (err: unknown) {
      let msg = 'Ошибка входа. Проверьте email и пароль.';
      if (err && typeof err === 'object' && 'response' in err) {
        const res = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response;
        const data = res?.data;
        if (data?.errors?.email?.length) {
          msg = data.errors.email[0];
        } else if (data?.message) {
          msg = data.message;
        }
      }
      setError(msg);
      setSubmitting(false);
    }
  };

  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
  }

  // Показываем прелоадер во время предзагрузки данных после авторизации
  if (isPrefetching) {
    return <AppPreloader />;
  }

  return (
    <div className="login-container">
      <div className="login-logo">
        <img src="/logo.svg" alt="A2K CRM" className="logo-image" />
      </div>
      
      <div className="login-card">
        <h1 className="login-title">Авторизация</h1>
        
        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}
          <div className="form-group">
            <input
              type="email"
              placeholder="Ваш email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              required
            />
          </div>
          
          <div className="form-group">
            <input
              type="password"
              placeholder="Ваш пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
              required
            />
          </div>
          
          <div className="form-remember">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="remember-text">Не завершать сессию</span>
          </div>
          
          <button type="submit" className="login-button" disabled={submitting}>
            {submitting ? 'Вход...' : 'Войти'}
          </button>
        </form>
        
        <a 
          href="#" 
          className="forgot-password"
          onClick={(e) => {
            e.preventDefault();
            setShowForgotPassword(true);
          }}
        >
          Забыли пароль?
        </a>
      </div>
    </div>
  );
};

export default Login;









